import { Box } from '@mui/material';
import { uniqBy } from 'lodash';
import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmployeeCalendarEvent,
  isEventInCalendarRange,
  processEvents,
} from '~/components/EmployeeCalendar/data';
import { EmployeeCalendarHeader } from '~/components/EmployeeCalendar/EmployeeCalendarHeader';
import {
  EmployeeCalendarLegend,
  EmployeeCalendarLegendProps,
} from '~/components/EmployeeCalendar/EmployeeCalendarLegend';
import {
  EmployeeCalendarRowGetters,
  EmployeeCalendarRows,
} from '~/components/EmployeeCalendar/EmployeeCalendarRows';
import { EmployeeCalendarCellGetters } from '~/components/EmployeeCalendar/EmployeeCalendarRows/EmployeeCalendarCell';
import { useEmployeeCalendar } from '~/components/EmployeeCalendar/useEmployeeCalendar';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { useSxStyles } from './useStyles';

export type EmployeeCalendarProps = {
  events: EmployeeCalendarEvent[];
  containerWidth: number;
  startDate: Date;
  endDate: Date;
  isLoading?: boolean;
  hideMonth?: boolean;
  showOutOfRange?: boolean;
  hideEmployeeName?: boolean;
} & EmployeeCalendarRowGetters &
  EmployeeCalendarCellGetters &
  EmployeeCalendarLegendProps;

export const EmployeeCalendar = forwardRef(
  (props: EmployeeCalendarProps, ref) => {
    const {
      events,
      containerWidth,
      startDate,
      endDate,
      onEmployeeClick,
      getEventContent,
      getEventColors,
      legends,
      legendsConfig,
      listEmployees,
      toggleLegendEvents,
      isLoading,
      hideMonth,
      showOutOfRange,
      hideEmployeeName,
    } = props;

    const { t } = useTranslation();

    const sxStyles = useSxStyles();
    const minSizeRender = containerWidth < 400;

    const { employeeNameHidden } = useMemo(() => {
      const eventEmployeeIds = uniqBy(events, 'employeeId');
      const hasMultipleEmployees = eventEmployeeIds.length > 1;

      return {
        employeeNameHidden: hideEmployeeName && !hasMultipleEmployees,
      };
    }, [events, hideEmployeeName]);

    const {
      datesInView,
      monthsInView,
      containerRef,
      calendarRange,
      navigation,
    } = useEmployeeCalendar({ startDate, endDate, showOutOfRange });

    const filteredEvents = useMemo(() => {
      return processEvents(events).filter((event) =>
        isEventInCalendarRange({
          event,
          calendar: calendarRange,
        }),
      );
    }, [events, calendarRange]);

    return (
      <Box sx={sxStyles.contentWrapper} ref={ref}>
        <Box sx={sxStyles.calendarHeaderWrapper} className={'calendar-header'}>
          {!employeeNameHidden && (
            <Box sx={minSizeRender ? sxStyles.minLeftCell : sxStyles.leftCells}>
              {t('name')}
            </Box>
          )}
          <Box ref={containerRef} sx={sxStyles.rightCells}>
            <EmployeeCalendarHeader
              datesInView={datesInView}
              monthsInView={monthsInView}
              navigation={navigation}
              showMonth={!hideMonth}
              hideOutOfRange={!showOutOfRange}
            />
          </Box>
        </Box>
        <Box sx={sxStyles.tableWrapper}>
          {isLoading ? (
            <LoadingView
              sx={sxStyles.loadingView}
              CircularProgressProps={{ size: 20 }}
            />
          ) : (
            <EmployeeCalendarRows
              minSizeRender={minSizeRender}
              events={filteredEvents}
              datesInView={datesInView}
              onEmployeeClick={onEmployeeClick}
              getEventContent={getEventContent}
              getEventColors={getEventColors}
              listEmployees={listEmployees}
              hideOutOfRange={!showOutOfRange}
              employeeNameHidden={employeeNameHidden}
            />
          )}
        </Box>
        <Box sx={sxStyles.footerWrapper} className={'calendar-legend'}>
          {!!legends && legends.length > 0 && (
            <EmployeeCalendarLegend
              legends={legends}
              toggleLegendEvents={toggleLegendEvents}
              legendsConfig={legendsConfig}
            />
          )}
        </Box>
      </Box>
    );
  },
);
EmployeeCalendar.displayName = 'EmployeeCalendar';
